import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { Link } from '@/i18n/navigation';
import IconFont from '@/components/ui/iconFont';

interface ListItemProps {
  /** 列表项标题文本 */
  title: string;
  /** 左侧图标插槽 */
  leftIcon?: ReactNode;
  /** 右侧内容插槽，显示在标题右侧 */
  rightContent?: ReactNode;
  /** 右侧额外信息插槽，显示在右侧内容和箭头之间 */
  extra?: ReactNode;
  /** 是否显示右侧箭头，默认为 true */
  arrow?: boolean;
  /** 点击事件回调函数 */
  onClick?: () => void;
  /** 点击埋点事件函数 */
  onClickTrack?: (e: { title: string, href: string; trackName: string; }) => void;
  /** 埋点名称 */
  trackName?: string;
  /** 链接地址，不传默认为 # */
  href?: string;
  /** 链接跳转前的回调函数 */
  onBeforeLinkJump?: () => void;
  /** 列表项类型，normal 高度 55px，short 高度 40px，默认为 normal */
  type?: 'normal' | 'short';

  /** 自定义容器类名 */
  className?: string;
  /** 标题文本的样式类名 */
  titleClassName?: string;
  /** 左侧图标的样式类名 */
  leftIconClassName?: string;
  /** 右侧容器的样式类名 */
  rightContainerClassName?: string;
  /** 右侧内容的样式类名 */
  rightContentClassName?: string;
  /** 额外信息的样式类名 */
  extraClassName?: string;
  /** 右侧箭头的样式类名 */
  arrowClassName?: string;

  /** 从父组件传递的容器样式类名（直接从父组件List拿的，所以优先级较低） */
  parentClassName?: string;
  /** 从父组件传递的标题样式类名（直接从父组件List拿的，所以优先级较低） */
  parentTitleClassName?: string;
  /** 从父组件传递的左侧图标样式类名（直接从父组件List拿的，所以优先级较低） */
  parentLeftIconClassName?: string;
  /** 从父组件传递的右侧内容样式类名（直接从父组件List拿的，所以优先级较低） */
  parentRightContentClassName?: string;
  /** 从父组件传递的额外信息样式类名（直接从父组件List拿的，所以优先级较低） */
  parentExtraClassName?: string;
  /** 从父组件传递的箭头样式类名（直接从父组件List拿的，所以优先级较低） */
  parentArrowClassName?: string;
}

interface ListProps {
  /** 子元素，通常是 ListItem 组件 */
  children: ReactNode;
  /** 列表容器的自定义样式类名 */
  className?: string;
  /** 传递给所有子元素的容器样式类名（优先级较低） */
  itemClassName?: string;
  /** 传递给所有子元素的标题样式类名（优先级较低） */
  itemTitleClassName?: string;
  /** 传递给所有子元素的左侧图标样式类名（优先级较低） */
  itemLeftIconClassName?: string;
  /** 传递给所有子元素的右侧内容样式类名（优先级较低） */
  itemRightContentClassName?: string;
  /** 传递给所有子元素的额外信息样式类名（优先级较低） */
  itemExtraClassName?: string;
  /** 传递给所有子元素的箭头样式类名（优先级较低） */
  itemArrowClassName?: string;
}

export const ListItem: React.FC<ListItemProps> = ({
  title,
  leftIcon,
  rightContent,
  extra,
  arrow = true,
  onClick,
  href = '#',
  onBeforeLinkJump,
  className,
  titleClassName,
  leftIconClassName,
  rightContentClassName,
  rightContainerClassName,
  extraClassName,
  arrowClassName,
  type = 'normal',
  onClickTrack,
  trackName = '',
  // 父组件传递的样式属性
  parentClassName,
  parentTitleClassName,
  parentLeftIconClassName,
  parentRightContentClassName,
  parentExtraClassName,
  parentArrowClassName,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClickTrack) {
      onClickTrack({ title, href, trackName });
    }
    if (onClick) {
      e.preventDefault(); // 禁用 Link 的默认跳转事件
      onClick();
    }
    if (href) {
      onBeforeLinkJump?.();
    }
  };

  return (
    <Link href={href} onClick={handleClick} draggable={false}>
      <div
        className={clsx(
          'flex box-border items-center cursor-pointer',
          type === 'normal' ? 'h-[55px]' : 'h-[40px]',
          parentClassName, // 父组件样式（优先级较低）
          className // 子组件样式（优先级较高）
        )}
      >
        {/* 左侧图标插槽 */}
        {leftIcon && (
          <div className={clsx("mr-1.5", parentLeftIconClassName, leftIconClassName)}>
            {leftIcon}
          </div>
        )}

        {/* 标题 */}
        <div className={clsx("flex-1 text-xs text-primary font-[600]", parentTitleClassName, titleClassName)}>
          {title}
        </div>

        {/* 右侧内容区域 */}
        <div className={clsx('flex items-center', rightContainerClassName)}>
          {rightContent && (
            <div className={clsx("text-tertiary text-xs font-medium", parentRightContentClassName, rightContentClassName)}>
              {rightContent}
            </div>
          )}

          {/* 额外信息 */}
          {extra && (
            <div className={clsx(parentExtraClassName, extraClassName)}>
              {extra}
            </div>
          )}

          {/* 箭头 */}
          {arrow && (
            <IconFont name="arrow-right" size={10} color='#a9b5cb' className={clsx(parentArrowClassName, arrowClassName)} />
          )}
        </div>
      </div>
    </Link>
  );
};

export const List: React.FC<ListProps> = ({
  children,
  className,
  itemClassName,
  itemTitleClassName,
  itemLeftIconClassName,
  itemRightContentClassName,
  itemExtraClassName,
  itemArrowClassName,
}) => {
  // 克隆子元素并传递样式属性
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement<ListItemProps>(child)) {
      return React.cloneElement(child, {
        parentClassName: itemClassName,
        parentTitleClassName: itemTitleClassName,
        parentLeftIconClassName: itemLeftIconClassName,
        parentRightContentClassName: itemRightContentClassName,
        parentExtraClassName: itemExtraClassName,
        parentArrowClassName: itemArrowClassName,
      });
    }
    return child;
  });

  return (
    <div className={clsx(
      'rounded-[10px] bg-surface',
      className
    )}>
      {childrenWithProps}
    </div>
  );
};

export default List;
